<template>
  <div class="patient-cases-page p-2">
    <div
      v-if="patientInformation"
      class="entity-card"
    >
      <b-row class="py-2 px-3">
        <b-col
          md="12"
          lg="3"
          class="d-flex align-items-center col-md-3"
        >
          <b-button class="btn__icon btn__icon--primary">
            <i
              class="far fa-user-circle mr-2 main"
              aria-hidden="true"
            />
          </b-button>
          <span
            class="entity-card__title d-flex align-items-center font-weight--medium truncated"
            qa="patient-cases-fullname"
          >
            {{ patientInformation.fullName }}
          </span>
          <vtBubble
            :number="patientInformation.incidentsCount"
            :type="true"
            :tooltip="` ${$t('grid.detail.hasAlarms')}`"
            class="ml-1"
            @click="openIncidentModal(null)"
          />
          <vtBubble
            :number="patientInformation.attachmentsCount"
            :type="false"
            :tooltip="` ${$tc('global.file',2)}`"
            @click="showPatientAttachmentTreeModal"
          />
        </b-col>
        <b-col
          cols="12"
          class="px-0 py-2 d-none d-sm-block d-lg-none"
        >
          <div class="horizontal-divider" />
        </b-col>
        <b-col
          v-if="globalParameters.mainPatientIdentifierType"
          class="entity-card__data"
        >
          <span class="entity-card__data--label">{{ globalParameters.mainPatientIdentifierType.name }}</span>
          <span
            class="entity-card__data--text"
            qa="patient-cases-main-identifier"
          >{{ patientInformation.mainPatientIdentifier ? patientInformation.mainPatientIdentifier.paiIdentifier : '-' }}</span>
        </b-col>
        <b-col
          v-if="globalParameters.secondaryPatientIdentifierType"
          class="entity-card__data"
        >
          <span class="entity-card__data--label">{{ globalParameters.secondaryPatientIdentifierType.name }}</span>
          <span
            class="entity-card__data--text"
            qa="patient-cases-secondary-identifier"
          >{{ patientInformation.secondaryPatientIdentifier ? patientInformation.secondaryPatientIdentifier.paiIdentifier : '-' }}</span>
        </b-col>
        <b-col
          class="entity-card__data"
        >
          <span class="entity-card__data--label">{{ $t('entities.patient.gender') }}</span>
          <span
            class="entity-card__data--text"
            qa="patient-cases-gender"
          >{{ patientInformation.gender.name }}</span>
        </b-col>
        <b-col
          class="entity-card__data"
        >
          <span class="entity-card__data--label">{{ $t('entities.patient.birthdate') }}</span>
          <span
            class="entity-card__data--text"
            qa="patient-cases-birthdate"
          >{{ patientInformation.birthdate | date }}</span>
        </b-col>
        <b-col
          class="entity-card__data"
        >
          <span class="entity-card__data--label">{{ $t('global.age') }}</span>
          <span
            class="entity-card__data--text"
            qa="patient-cases-birthday"
          >{{ patientTimeOld }}</span>
        </b-col>
      </b-row>
    </div>
    <div class="grid-container mt-3">
      <grid-header :gridtitle="`${$tc('global.case',2)} (${rowData.length})`">
        <b-button
          v-if="$_canAccessRegistry()"
          v-tooltip="$t('component.modalPatientCases.openCase')"
          :disabled="!canBeVisualized"
          class="btn__icon btn__icon--primary px-3"
          @click="onOpenCaseButtonClick"
        >
          <i
            class="far fa-chart-network"
            aria-hidden="true"
          />
        </b-button>
        <b-button
          v-if="$_canPrintCaseReport()"
          v-tooltip="$t('registry.btn.viewAnaReport')"
          :disabled="selectedRows.length !== 1"
          class=" btn__icon btn__icon--primary px-2"
          @click="onPrintCaseReport()"
        >
          <i
            class="far fa-file-contract"
            aria-hidden="true"
          />
        </b-button>
        <b-button
          v-if="$_canPrintPreviousCaseReport()"
          v-tooltip="$t('component.modalPatientCases.previousCasesReport')"
          class=" btn__icon btn__icon--primary pl-2 pr-0"
          @click="exportPatientPreviousCasesReportPDF"
        >
          <i
            class="far fa-file-export"
            aria-hidden="true"
          />
        </b-button>
      </grid-header>
      <vt-grid
        class="ps-grid patient-cases-detail"
        :detail-cell-renderer="detailCellRenderer"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :framework-components="frameworkComponents"
        master-detail
        :detail-cell-renderer-params="{patId}"
        :row-data="rowData"
        :detail-row-height="500"
        component-code="uicomponent.registry.patientcasegrid"
      />
    </div>
    <modal-wrapper ref="modalPatientCard">
      <modal-patient-card
        v-if="patientInformation"
        id="modalPatientCard"
        :patient-id="Number(patId)"
      />
    </modal-wrapper>
    <modal-wrapper ref="associatedCasesListModal">
      <modal-associated-cases-list />
    </modal-wrapper>
    <modal-wrapper ref="incidentModalTreeWrapper">
      <common-incident-modal-tree v-if="patientInformation" />
    </modal-wrapper>
    <!-- TODO: Impossible open attachment modal with modal-wrapper container, need to review in all product -->
    <common-attachment-tree-modal
      v-if="patientInformation"
      ref="attachmentTreeModal"
      :patient="patientInformation"
      :laboratory-id="selectedLaboratoryLabId"
    />
  </div>
</template>

<script>
import ModalPatientCard from '@/components/modals/ModalPatientCard'
import VtBubble from '@/components/common/VtBubble'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import GridHeader from '@/components/GridHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import caseServices from '@/api/resources/common/commonCaseServices'
import patientServices from '@/api/resources/common/commonPatientServices'
import patientCasesColumnsDef from '@/components/patientPreviousCases/config/patientCasesColumnsDef'
import PatientTabsCellRenderer from '@/components/patientPreviousCases/PatientTabsCellRenderer'
import ModalAssociatedCasesList from '@/components/patientPreviousCases/ModalAssociatedCasesList'
import Encoder from '@/utils/encoder'
import patientResource from '@/api/resources/PatientResource'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { ModuleOperationGuardMixin } from '@/api/operations/mixins/guards/module-operation-guard.mixin'
import CommonAttachmentTreeModal from '@/components/attachments/CommonAttachmentModalTree'
import CommonIncidentModalTree from '@/components/incidentsModal/CommonIncidentModalTree'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { HelperUtils } from '@/shared/util/helper-utils'

export default {
    name: 'PatientCases',
    components: {
        ModalPatientCard,
        VtBubble,
        GridHeader,
        VtGrid,
        ModalAssociatedCasesList,
        CommonAttachmentTreeModal,
        CommonIncidentModalTree
    },
    mixins: [
        CaseOperationGuardMixin,
        ModuleOperationGuardMixin
    ],
    data() {
        return {
            patientInformation: null,
            selectedRows: [],
            rowData: [],
            columnDefs: patientCasesColumnsDef(),
            gridApi: null,
            isHide: true,
            patId: null,
            patient: null,
            encoder: new Encoder(),
            contentOverride: null,
            selected: null,
            detailCellRenderer: 'detailCellRenderer',
            frameworkComponents: { detailCellRenderer: PatientTabsCellRenderer },
            options: [
                { value: null, text: '' },
                { value: 'a', text: 'Opción 1' },
                { value: 'b', text: 'Opción 2' },
                { value: 'b', text: 'Opción 3' }
            ],
            selectedLaboratoryLabId: null
        }
    },
    computed: {
        gridOptions() {
            return {
                pagination: false,
                getContextMenuItems: this.getContextMenuItems,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                floatingFilter: false,
                sideBar: gridSideBarConfig,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged
            }
        },
        displayContent() {
            return this.contentOverride || this.content
        },
        patientTimeOld() {
            return this.patientInformation && this.getPatientTimeOld(this.patientInformation.birthdate)
        },
        canBeVisualized() {
            return this.selectedRows.length === 1 && this.selectedRows[0].metadata.operationGuards.canBeVisualized
        }
    },
    async mounted() {
        this.patId = this.$route.params.patId
        this.selectedLaboratoryLabId = this.$store.getters.labId
        const [err, response] = await this.toCall(patientServices.getPatientInformation(this.selectedLaboratoryLabId, this.patId))
        if (err) {
            this.showErrorModalGlobal()
        }
        this.patientInformation = response
    },
    methods: {
        onOpenCaseButtonClick() {
            this.openStudySelected(this.selectedRows[0])
        },
        onOpenCaseContextMenuOptionClick(caseStudy) {
            this.openStudySelected(caseStudy)
        },
        showAssociatedCasesListModal(params) {
            this.$refs.associatedCasesListModal.show(params?.node.data.id)
        },
        openCase(caseStudy) {
            const routeData = this.$router.resolve({ name: 'registryEditCase', params: { id: caseStudy.id } })
            window.open(routeData.href, '_blank')
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                const caseStudy = params?.node.data
                return [
                    {
                        name: this.$t('component.modalPatientCases.openCase'),
                        action: () => this.onOpenCaseContextMenuOptionClick(caseStudy),
                        cssClasses: this.$_canAccessRegistry() && this.canBeVisualized ? '' : ['not-show']
                    },
                    {
                        name: this.$t('registry.btn.viewAnaReport'),
                        action: () => this.getCaseReportPDF(caseStudy.laboratory.labId, caseStudy.id, caseStudy.casenumber),
                        cssClasses: this.$_canPrintCaseReport() && this.$_canBeCaseReportPrinted(params?.node.data) && this.selectedRows.length === 1 ? '' : ['not-show']
                    },
                    {
                        name: this.$t('component.modalPatientCases.previousCasesReport'),
                        action: () => this.exportPatientPreviousCasesReportPDF(),
                        cssClasses: this.$_canPrintPreviousCaseReport() ? '' : ['not-show']
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.getRowData()
        },
        async getRowData() {
            if (!this.cases) {
                this.rowData = await caseServices.getPreviousCases(
                    this.selectedLaboratoryLabId,
                    this.patId
                )
            } else {
                this.rowData = this.cases
            }
        },
        show() {
            this.$refs.bModal.show()
        },
        showModal() {
            this.$refs['modalPatientCard'].show()
        },
        hide() {
            this.$refs.bModal.hide()
        },
        onHide() {
            this.contentOverride = null
            this.$emit('onHide')
        },
        async exportPatientPreviousCasesReportPDF() {
            const blob =
                await this.$_toCallSecure(
                    patientResource.getPreviousCaseAPReport(this.$store.getters.labId, this.patId, false))
            if (blob) {
                const newWindowTabTitle = `${this.patientInformation.mainPatientIdentifier ? this.patientInformation.mainPatientIdentifier.paiIdentifier : '-'} - ${this.$tc('global.caseReport')}`
                const commonOpenFileParams = {
                    blob,
                    newWindowTabTitle
                }
                this.$_openFile(commonOpenFileParams)
            }
        },
        async onPrintCaseReport() {
            if (this.selectedRows.length) {
                await this.getCaseReportPDF(this.selectedRows[0].laboratory.labId, this.selectedRows[0].id, this.selectedRows[0].casenumber)
            }
        },
        async getCaseReportPDF(laboratoryId, caseId, caseLabel) {
            const getCaseReportPDFParams = {
                laboratoryId,
                caseId,
                caseLabel,
                print: false
            }
            await HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
        },
        openIncidentModal(defaultOption) {
            this.$refs.incidentModalTreeWrapper.show([defaultOption, this.patientInformation.id])
        },
        showPatientAttachmentTreeModal() {
            this.$refs.attachmentTreeModal.showPatient()
        },
        async openStudySelected(studySelected) {
            if (studySelected) {
                const navigationConfirmed = await this.$_confirmNavigationBecauseOfLaboratoryChange(studySelected.laboratory.labId)
                if (navigationConfirmed) {
                    const routeData = this.$router.resolve({ name: 'registryEditCase', params: { id: studySelected.id } })
                    window.open(routeData.href, '_blank')
                }
            }
        }
    }
}
</script>

<style lang="scss">
.patient-cases-page {
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.patient-cases-page .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
  letter-spacing: 0;
  font-weight: 700;
  font-size: font-size(small);
}

.patient-cases-page .vt-tabs .nav-tabs .nav-link.active,
.vt-tabs--small .nav-tabs .nav-link.active {
  border-bottom: 2px solid vt-color(primary);
}

.patient-cases-page .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
}

.patient-cases-page .nav-tabs .nav-link {
  border-radius: 2px;
}

.patient-cases-page .modal-dialog {
  max-width: 75%;
}

.patient-cases-page .modalPatientCard .modal-dialog {
  max-width: 60%;
}

.patient-cases-page .ps-grid {
  overflow-y: auto;
  height: 100%;
}

.patient-cases-page .flex-btn {
  width: 5% !important;
}

.patient-cases-page .ag-status-bar {
  display: none;
}

.patient-cases-page .ps-grid.patient-cases-detail .ag-full-width-row {
  background: #FFF !important;
}

.patient-cases.ag-cell-value {
    overflow-y: auto;
}
@media (max-width: 1300px) {
  .patient-cases-page .modal-dialog {
    max-width: 96%;
  }

  .patient-cases-page .modalPatientCard .modal-dialog {
    max-width: 80%;
  }
}
</style>

<style lang="scss" scoped>
.title {
  font-size: font-size(normal);
  color: vt-color(primary);
}

.patient-card {
  height: 40px;
  box-shadow: 1px 1px 5px 0 rgba(125, 125, 125, 0.64);
}

.patient-card .card-info span {
  font-size: font-size(very-small);
  color: vt-color(dark-text);
}

.patient-card .main-info i {
  font-size: font-size(big);
}

.patient-card .main-info span {
  font-size: font-size(very-small);
  color: vt-color(dark-text);
}

.patient-card .floating {
  right: 10px;
  top: 0;
}

.patient-card .card-body {
  display: flex;
  align-items: center;
  padding-right: 0;
}

.header-grid {
  background-color: #fff !important;
  border: 1px solid #d8d8d8;
  border-left: 4px solid vt-color(primary);
}

.check-text {
  font-size: 0.9rem;
}
</style>
